import style from "./template-link-modal.module.css";
import tracking from "frontend/tracking";
import React, { useEffect, useRef, useState } from "react";
import consts from "shared/consts";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { useBoardValue } from "frontend/state/board-state";
import { useAtomValue } from "jotai";
import { stageRefAtom } from "state-atoms/general-atoms";
import { isStageReadyAtom, posScaleAtom } from "state-atoms/stage-atoms";
import { createTemplateLink, updateTemplateLink, deactivateTemplateLink, getTemplateLink } from "frontend/api";
import {
  createTemplateLinkSchema,
  TemplateLink,
  updateTemplateLinkSchema,
} from "shared/datamodel/schemas/template-link";
import StyledButton from "frontend/ui-components/styled-button";
import MiniStage from "frontend/canvas-designer-new/mini-stage";
import AppLoader from "frontend/loader/app-loader";
import Toast from "frontend/ui-components/toast/toast";
import { format } from "timeago.js";
import useOutsideRef from "frontend/utils/click-outside-handler";
import TemplateNotSupportedModal from "./template-not-supported-modal";
import { getCanvasDataForTemplate } from "frontend/template-creation";
import { getPathPrefix } from "../utils/getPathPrefix";

export default function TemplateLinkModal({
  onDismiss,
  syncService,
  isCanvasContainsIntegrationElements,
}: {
  onDismiss: () => void;
  syncService: SyncService<RW> | null;
  isCanvasContainsIntegrationElements: (syncService: SyncService<RW> | null) => Promise<boolean>;
}) {
  const [{ board }] = useBoardValue();

  const [templateLinkData, setTemplateLinkData] = useState<Record<string, any>>({});
  const [isCreating, setCreating] = useState(false);
  const [template, setTemplate] = useState<TemplateLink | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isLoadingTemplate, setLoadingTemplate] = useState(true);
  const [isLoadingIntegrations, setLoadingIntegrations] = useState(true);
  const [showCreateScreen, setShowCreateScreen] = useState(true);

  const isStageReady = useAtomValue(isStageReadyAtom);
  const stageRef = useAtomValue(stageRefAtom);
  const posScale = useAtomValue(posScaleAtom);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [triggerToast, setTriggerToast] = useState(false);
  const [triggerErrorToast, setTriggerErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const modalRef = useRef(null);
  useOutsideRef(modalRef, onDismiss, {
    preventScroll: false,
  });

  useEffect(() => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.TEMPLATES, "save-template-as-a-link-shown", board?.documentId);
  }, []);

  useEffect(() => {
    if (!previewImage) {
      createPreviewImage();
    }
  }, [stageRef, posScale]);

  async function checkIntegrations() {
    const hasIntegrations = await isCanvasContainsIntegrationElements(syncService);
    if (template) {
      setShowCreateScreen(true);
    } else if (hasIntegrations) {
      setShowCreateScreen(false);
    }
    setLoadingIntegrations(false);
  }

  async function createPreviewImage() {
    const snapShotUrl = await stageRef?.current?.getSnapshotImage({
      withWatermark: false,
      quality: 1,
      position: posScale,
      scale: posScale.scale,
      maxSize: 500,
    });
    setPreviewImage(snapShotUrl ?? null);
  }

  useEffect(() => {
    if (!isStageReady) {
      return;
    }
    if (!template) {
      getCanvasDataForTemplate(syncService, true).then((data) => {
        if (data) {
          setTemplateLinkData(data);
        }
      });
    } else {
      setTemplateLinkData(template.data);
    }
    checkIntegrations();
  }, [isStageReady, template]);

  useEffect(() => {
    async function fetchTemplateLink(boardDocumentId: string): Promise<TemplateLink | null> {
      setLoadingTemplate(true);
      try {
        const template = await getTemplateLink({ id: boardDocumentId, type: "board" });
        return template;
      } catch {
        return null;
      } finally {
        setLoadingTemplate(false);
      }
    }
    if (board?.documentId) {
      fetchTemplateLink(board.documentId).then((template) => {
        if (template) {
          setTemplate(template);
        }
      });
    }
  }, [board?.documentId]);

  function renderCloseIcon() {
    return (
      <svg
        className={style.closeIcon}
        onClick={onDismiss}
        viewBox="0 0 26 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        data-testid="close-template-link-modal"
      >
        <path
          d="M6.36407 6.36396L19.092 19.0919M19.092 6.36396L6.36407 19.0919"
          stroke="currentColor"
          strokeWidth="1.74"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  async function onCreateTemplateLink() {
    try {
      const newTemplateLink = createTemplateLinkSchema.parse({
        name: board?.name,
        createdFromBoardDocumentId: board?.documentId,
        data: templateLinkData,
      });
      setCreating(true);
      const template = await createTemplateLink(newTemplateLink);
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.TEMPLATES, // category
        "create_template_link", // action
        board?.documentId,
        template.linkId,
        board?.name
      );
      setTemplate(template);
    } catch (error) {
      let errorMessage = "Failed to create template link";

      // Check if it's a 403 error
      if (error instanceof Error && (error.message.includes("403") || (error as any).status === 403)) {
        errorMessage = "You don't have permission to create a template link from this board";
      }
      setErrorMessage(errorMessage);
      setTriggerErrorToast((prev) => !prev);
    } finally {
      setCreating(false);
    }
  }

  async function onUpdateLink() {
    try {
      const data = await getCanvasDataForTemplate(syncService, true);
      const updateTemplate = updateTemplateLinkSchema.parse({ name: board?.name, linkId: template?.linkId, data });
      if (data) {
        setTemplateLinkData(data);
      }
      const templateLink = await updateTemplateLink(updateTemplate);
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.TEMPLATES, // category
        "update_template_link", // action
        board?.documentId,
        templateLink.linkId,
        board?.name
      );
      setTemplate(templateLink);
      setTriggerToast((prev) => !prev);
    } catch (error) {
      let errorMessage = "Failed to update template link";

      if (error instanceof Error) {
        // Check if it's a 403 error
        if (error.message.includes("403") || (error as any).status === 403) {
          errorMessage = "Only the creator of the template link can update it";
        } else {
          errorMessage = error.message;
        }
      }
      setErrorMessage(errorMessage);
      setTriggerErrorToast((prev) => !prev);
    }
  }

  async function onDeleteTemplateLink() {
    try {
      if (!template?.linkId) return;
      await deactivateTemplateLink(template.linkId);
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.TEMPLATES, // category
        "delete_template_link", // action
        board?.documentId,
        template?.linkId,
        board?.name
      );
      setTemplate(null);
    } catch {
      setErrorMessage("Failed to delete template link");
      setTriggerErrorToast((prev) => !prev);
    }
  }

  function renderCreateFooter() {
    return (
      <>
        <div className={style.buttonContainer}>
          <StyledButton
            title="Create template link"
            onClick={onCreateTemplateLink}
            loading={isCreating}
            loadingTitle="Creating template"
            customStyle={{
              fontSize: 16,
              fontWeight: 400,
              height: 38,
              width: 385,
            }}
          />
        </div>
        <div className={style.note}>This link can be deleted at any time</div>
      </>
    );
  }

  function renderEditFooter() {
    const link = `${process.env.APP_URL!}/templates/${template?.linkId}`;
    return (
      <>
        <div className={style.updateContainer}>
          <span className={style.updateLink} onClick={onUpdateLink}>
            Update template Link
          </span>
        </div>
        <div className={style.buttonContainer}>
          <div className={style.linkContainer}> {link} </div>
          <StyledButton
            title={copiedLink ? "Link Copied!" : "Copy Link"}
            onClick={() => {
              navigator.clipboard.writeText(link);
              setCopiedLink(true);
              setTimeout(() => setCopiedLink(false), 5000); // Reset after 5 seconds
              tracking.trackEvent(
                consts.TRACKING_CATEGORY.TEMPLATES, // category
                "copy_template_link", // action
                board?.documentId,
                template?.linkId,
                board?.name
              );
            }}
            loading={isCreating}
            loadingTitle="Link Copied"
            customStyle={{
              fontSize: 13,
              fontWeight: 400,
              height: 38,
              width: 117,
              padding: 0,
            }}
            icon={getPathPrefix("/images/link-icon-white.svg")}
          />
        </div>
        <div className={style.delete} onClick={onDeleteTemplateLink}>
          Delete template link
        </div>
      </>
    );
  }

  function renderCreateOrEditTemplateLink() {
    return (
      <>
        {renderCloseIcon()}
        <div className={style.fieldsContainer}>
          <div className={style.title}>{template ? "Your template link" : "Save as link"}</div>
          <div className={style.subtitle}>Anyone with the link can use this canvas as a template</div>
          <div className={style.previewContainer} style={{ marginBottom: template ? 0 : 12 }}>
            {template && <p className={style.lastUpdate}>{`Template last updated: ${format(template.updatedAt)}`}</p>}
            <div style={{ border: "1px #DADCE0 solid", height: 278, overflow: "hidden" }}>
              {templateLinkData ? (
                <MiniStage width={465} height={276} templateId={""} templateData={templateLinkData} />
              ) : (
                previewImage && <img src={previewImage} />
              )}
            </div>
          </div>
          <div className={style.footer}>{template ? renderEditFooter() : renderCreateFooter()}</div>
        </div>
        <Toast text={"Template link updated"} trigger={triggerToast} />
        <Toast text={errorMessage} trigger={triggerErrorToast} />
      </>
    );
  }

  return (
    <div className={style.container} ref={modalRef}>
      {isLoadingTemplate || isLoadingIntegrations ? (
        <AppLoader />
      ) : showCreateScreen ? (
        renderCreateOrEditTemplateLink()
      ) : (
        <TemplateNotSupportedModal
          onDismiss={onDismiss}
          createTemplateWithoutIntegrations={() => setShowCreateScreen(true)}
        />
      )}
    </div>
  );
}
